// ---------------------------------------------------------------------------
// The sub-basins GeoJSON layer's onEachFeature — tooltip + mode-dependent
// click dispatch (draw/select/results/series/edit) + hover restyle. Split
// out of SubBasinMap.tsx (see project_evalhid_code_audit.md's ongoing "god
// module" breakup), same sibling-file convention already used there for
// subBasinMapEntities.ts/subBasinMapStyle.ts.
// ---------------------------------------------------------------------------
import { DomEvent, type Layer } from "leaflet";
import type { useT } from "../i18n/hooks.ts";
import type { SubBasin, EvalHidProject } from "../model/types.ts";
import type { SubBasinFeature } from "../model/gisLayer.ts";
import type { DrawingTool } from "./drawingTools.ts";
import type { MapResultsMode, MapSeriesMode, MapSelectionMode } from "./mapModes.ts";
import { subBasinOf, popupContent } from "./subBasinMapEntities.ts";
import { hoverStyleFor, isPath, styleFor, visualStateOf } from "./subBasinMapStyle.ts";
import type { AssignmentColorMode } from "../model/assignmentColors.ts";
import { colorFor } from "../model/assignmentColors.ts";

interface OnEachFeatureOptions {
  t: ReturnType<typeof useT>;
  project: EvalHidProject;
  problemsOf: (c: SubBasin) => string[];
  selectionMode: MapSelectionMode | undefined;
  /**
   * Current selection, always up to date — unlike `selectionMode` (which
   * stays fixed to the closure at the moment this click handler was
   * created), this ref is updated on every render without forcing the
   * GeoJSON layer to remount. Needed because `signature`/`key` no longer
   * includes the selection's content (see the comment next to `signature`,
   * SubBasinMap.tsx): without this ref, after the first click, (de)selecting
   * on each following click would be computed against a stale Set.
   */
  selectionRef: { current: Set<number> | undefined };
  /** See the comment next to its declaration (SubBasinMap): "is it selected?" recomputed on the fly, never closed over at mount. */
  isSelectedRef: { current: (subBasin: SubBasin | null) => boolean };
  drawingMode: DrawingTool | null | undefined;
  resultsMode: MapResultsMode | undefined;
  seriesMode: MapSeriesMode | undefined;
  onEditSubBasin: ((subBasin: SubBasin) => void) | undefined;
  colorMode: AssignmentColorMode | undefined;
  colorMap: Map<number, string> | undefined;
}

/**
 * How long a plain click in selection mode waits before actually toggling —
 * long enough for a following second click (the DOM always fires two real
 * "click" events before "dblclick") to cancel it instead. Real annoyance
 * this fixes: toggling on click1 then back off on click2 nets out to the
 * same selection state, but each toggle re-renders and re-styles the
 * polygon in between — a visible flash of (de)selecting right before the
 * edit dialog opens, even though nothing about the selection actually
 * changes. 250ms is short enough that a genuine single click still feels
 * immediate, comfortably inside common double-click detection windows.
 */
const CLICK_TOGGLE_DELAY_MS = 250;

export function onEachFeature({
  t,
  project,
  problemsOf,
  selectionMode,
  selectionRef,
  isSelectedRef,
  drawingMode,
  resultsMode,
  seriesMode,
  onEditSubBasin,
  colorMode,
  colorMap,
}: OnEachFeatureOptions) {
  // Scoped per feature (a fresh closure per polygon, see the returned
  // function below) — never shared across sub-basins, so one polygon's
  // pending toggle can't be cancelled by clicking a different one.
  let pendingToggle: ReturnType<typeof setTimeout> | null = null;

  return (feature: SubBasinFeature, layer: Layer) => {
    const subBasin = subBasinOf(project, feature);
    const problems = subBasin ? problemsOf(subBasin) : [];
    const state = visualStateOf(subBasin, problemsOf);
    const withSnow = subBasin?.snowZoneCode != null;
    const colorFill =
      colorMode && colorMap && subBasin ? colorFor(colorMap, colorMode.field, subBasin) : undefined;

    // Tooltip on hover, always (in all four modes): in editing/results mode
    // the click switches to (de)selecting, and in drawing mode the click
    // switches to placing a vertex, so without this there would be no way
    // to see a sub-basin's data without selecting it/drawing over it or
    // opening its edit dialog. `sticky` so it follows the cursor over large
    // polygons.
    layer.bindTooltip(popupContent(t, project, feature, subBasin, problems), {
      sticky: true,
      direction: "top",
    });

    if (drawingMode) {
      // In drawing mode, clicking doesn't select or edit: the click (even
      // over an existing sub-basin) must reach the map's own handler
      // (DrawEvents) unchanged, to place a vertex there.
    } else if (selectionMode) {
      // In editing mode, clicking (de)selects instead of editing — same
      // mechanism as AssignmentMap.tsx, now on the main map. Shift+click
      // ADDS, Ctrl+click REMOVES — same semantics (and never-toggles
      // reasoning) as Shift+drag/Ctrl+drag (RectangleSelection.tsx), just
      // for one sub-basin instead of a rectangle's worth. Without any
      // modifier, a plain click still toggles, for a quick single
      // (de)select without holding anything.
      layer.on("click", (e) => {
        if (!subBasin) return;
        // Deferred, not immediate — see CLICK_TOGGLE_DELAY_MS's own
        // comment: gives a following second click (about to become a
        // dblclick) a chance to cancel this before it ever visibly
        // (de)selects anything.
        if (pendingToggle) clearTimeout(pendingToggle);
        const shiftKey = e.originalEvent.shiftKey;
        const ctrlOrMeta = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
        pendingToggle = setTimeout(() => {
          pendingToggle = null;
          const copy = new Set(selectionRef.current);
          if (shiftKey) copy.add(subBasin.subBasinCode);
          else if (ctrlOrMeta) copy.delete(subBasin.subBasinCode);
          else if (copy.has(subBasin.subBasinCode)) copy.delete(subBasin.subBasinCode);
          else copy.add(subBasin.subBasinCode);
          selectionMode.onCambiarSeleccion(copy);
        }, CLICK_TOGGLE_DELAY_MS);
      });
      // Double-click opens the edit dialog directly, without leaving
      // selection mode — cancels whichever of the two intervening "click"
      // events' toggle is still pending (a dblclick is always preceded by
      // two real clicks in the DOM), so selection is left completely
      // untouched instead of visibly flashing (de)selected first. Stops
      // propagation so Leaflet's own default (double-click zooms the map)
      // doesn't ALSO fire — same trap as any Leaflet layer double-click.
      layer.on("dblclick", (e) => {
        if (pendingToggle) {
          clearTimeout(pendingToggle);
          pendingToggle = null;
        }
        DomEvent.stopPropagation(e);
        if (subBasin) onEditSubBasin?.(subBasin);
      });
    } else if (resultsMode) {
      // In results mode, clicking selects that sub-basin in the Results
      // panel (a single one, not a set — see MapResultsMode).
      layer.on("click", () => {
        if (!subBasin) return;
        resultsMode.onSelect({ level: "subBasin", id: subBasin.subBasinCode });
      });
    } else if (seriesMode) {
      // "Input series" tab open: clicking filters the series charts down to
      // this sub-basin instead of (de)selecting or editing — see
      // SeriesPanel.tsx/MapSeriesMode.
      layer.on("click", () => {
        if (!subBasin) return;
        seriesMode.onSelect({ kind: "subBasin", id: subBasin.subBasinCode });
      });
    } else if (onEditSubBasin) {
      // Outside any special mode, clicking opens the edit dialog directly
      // (EditSubBasinDialog) — no need to go through "Edit project"
      // just to view/edit a sub-basin. The pencil is reserved for
      // multi-selection and the catalogs (zones, outlets...), not this.
      layer.on("click", () => {
        if (subBasin) onEditSubBasin(subBasin);
      });
    }
    layer.on(
      "mouseover",
      () =>
        isPath(layer) &&
        layer.setStyle(hoverStyleFor(state, isSelectedRef.current(subBasin), withSnow, colorFill))
    );
    layer.on(
      "mouseout",
      () =>
        isPath(layer) &&
        layer.setStyle(styleFor(state, isSelectedRef.current(subBasin), withSnow, colorFill))
    );
  };
}
